import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Mail, Lock, User, ArrowRight } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/2bd9e08b-988f-44f1-ac8a-b908ec9312fb/images/e9bdddaf7639853e71a342ea671e29a39bcc721ee6a6d6eb08c6f8f9037e036d.png";

export default function LoginPage({ isRegister }) {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(isRegister ? "register" : "login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let result;
    if (tab === "login") {
      result = await login(form.email, form.password);
    } else {
      if (!form.name.trim()) { setError("Name is required"); setLoading(false); return; }
      result = await register(form.name, form.email, form.password);
    }
    setLoading(false);
    if (result.success) {
      navigate("/dashboard", { replace: true });
    } else {
      setError(result.error);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/80 to-zinc-950/40" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo.png" alt="Castfolio" className="w-14 h-14 rounded-xl shadow-[0_0_20px_rgba(225,29,72,0.4)]" />
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">Castfolio</h1>
              <p className="text-xs text-rose-400 uppercase tracking-[0.3em]">Diecast Portfolio</p>
            </div>
          </div>
          <p className="text-zinc-300 text-lg max-w-md font-body leading-relaxed">
            Track, value, and showcase your 1:64 diecast collection — from Kaido House and Mini GT to Hot Wheels, Tarmac Works, and Inno64. Your portfolio, elevated.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/logo.png" alt="Castfolio" className="w-12 h-12 rounded-lg" />
            <div>
              <h1 className="font-heading text-xl font-bold text-zinc-100">Castfolio</h1>
              <p className="text-[10px] text-rose-500 uppercase tracking-[0.2em]">Diecast Portfolio</p>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-900 border border-zinc-800">
              <TabsTrigger data-testid="login-tab" value="login" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100">Sign In</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="login-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500 focus:ring-rose-500/20"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="login-password"
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500 focus:ring-rose-500/20"
                      placeholder="Enter password"
                      required
                    />
                  </div>
                </div>
                {error && <p data-testid="auth-error" className="text-rose-400 text-sm">{error}</p>}
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-rose-600 hover:bg-rose-500 text-white font-medium shadow-[0_0_15px_rgba(225,29,72,0.3)] hover:shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all">
                  {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Name</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="register-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500 focus:ring-rose-500/20"
                      placeholder="Your name"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="register-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500 focus:ring-rose-500/20"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      data-testid="register-password"
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500 focus:ring-rose-500/20"
                      placeholder="Min 6 characters"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                {error && <p data-testid="auth-error" className="text-rose-400 text-sm">{error}</p>}
                <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-rose-600 hover:bg-rose-500 text-white font-medium shadow-[0_0_15px_rgba(225,29,72,0.3)] hover:shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all">
                  {loading ? "Creating account..." : "Create Account"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800" /></div>
              <div className="relative flex justify-center text-xs"><span className="px-3 bg-zinc-950 text-zinc-500 uppercase tracking-wider">or continue with</span></div>
            </div>
            <Button
              data-testid="google-login-btn"
              variant="outline"
              className="w-full mt-4 bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 hover:border-zinc-700 transition-all"
              onClick={handleGoogleLogin}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Google
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
